import {
  AppQuizUsageQuestionEssay,
  AppQuizUsageQuestionMatching,
  AppQuizUsageQuestionMissingWord,
  AppQuizUsageQuestionMultipleChoice,
  AppQuizUsageQuestionNumerical,
  AppQuizUsageQuestionSingleChoice,
  AppQuizUsageQuestionTrueFalse,
  AppQuiz_content_QuizContent,
  AppQuiz_content_QuizContent_questions,
} from 'globalTypes'
import {
  cloneDeep,
  filter,
  find,
  findIndex,
  isArray,
  isEqual,
  keyBy,
  keys,
  map,
  mapValues,
  shuffle,
  size,
  slice,
  split,
  trim,
} from 'lodash'
import { v4 as uuidv4 } from 'uuid'

interface Answers {
  [id: string]: any
}

interface Points {
  [id: string]: number
}

export interface ReducerState {
  /**
   * Used to store answers by question id.
   */
  answers: Answers
  /**
   * Original quiz content.
   */
  content: AppQuiz_content_QuizContent
  /**
   * Questions group, initialized on quiz start.
   */
  questions: AppQuiz_content_QuizContent_questions[]
  /**
   * Define if the quiz was started.
   */
  startedAt?: Date
  /**
   * Session identifier, used to store an retrieve temporary status.
   */
  sessionId?: string
  /**
   * Remaining time, defined by time limit.
   */
  remainingTime?: number
  /**
   * Unique identifier of the current question, if undefined the quiz is not started.
   */
  currentQuestionId?: string
  /**
   * When is completed there's no current question.
   */
  completed?: boolean
  /**
   * Points, indexed by question ID.
   */
  points: Points
}

export const getInitialState = (
  content: AppQuiz_content_QuizContent
): ReducerState => {
  if (Boolean(content.restartFromBegin) && Boolean(content.session)) {
    return {
      answers: JSON.parse(content.session.answers),
      completed: content.session.completed || false,
      content,
      points: content.session.points,
      questions: content.session.questions,
      sessionId: content.session?.id || undefined,
    }
  }

  if (Boolean(content.extractProgress)) {
    const parsed = JSON.parse(content.extractProgress)

    // if the quiz was updated than we could have
    // an invalid state
    if (
      Boolean(parsed.startedAt) &&
      new Date(parsed.startedAt) >= new Date(content.updatedAt)
    ) {
      return {
        ...parsed,
        completed: content.session?.completed || false,
        content,
        sessionId: content.session?.id || undefined,
      }
    }
  }

  const initialState = {
    answers: {},
    completed: content.session?.completed || false,
    content,
    points: {},
    questions: [],
    sessionId: content.session?.id || undefined,
  }

  // if it's a quick quiz than there's no cover
  if (content.quizSubtype === 'quick_quiz') {
    return start(initialState)
  }

  return initialState
}

export const reducer = (state: ReducerState, action) => {
  switch (action.type) {
    case 'start':
      return start(state)
    case 'answer':
      return answer(state, action.questionId, action.data)
    case 'go-to-question':
      return goToQuestion(state, action.questionId)
    case 'set-timer':
      return {
        ...state,
        remainingTime: action.value,
      }
    case 'complete':
      return complete(state)
    case 'repeat':
      return start(state)
    case 'updateMySessions':
      return {
        ...state,
        content: {
          ...state.content,
          mySessions: action.value,
        },
      }
    default:
      throw new Error()
  }
}

export const goToQuestion = (state: ReducerState, questionId: string) => {
  return {
    ...state,
    currentQuestionId: questionId,
  }
}

export const answer = (state: ReducerState, questionId: string, data) => {
  return {
    ...state,
    answers: {
      ...(state.answers || {}),
      [questionId]: data,
    },
  }
}

export const complete = (state: ReducerState) => {
  return {
    ...state,
    completed: true,
  }
}

export const getCurrentQuestionIndex = (
  state: ReducerState,
  currentQuestionId: string
): number =>
  findIndex(state.questions, ({ id }: any) => id === currentQuestionId)

export const hasPreviousQuestion = (
  state: ReducerState,
  currentQuestionId?: string
): boolean =>
  typeof getPreviousQuestion(state, currentQuestionId) !== 'undefined'

export const getPreviousQuestion = (
  state: ReducerState,
  currentQuestionId?: string
): undefined | AppQuiz_content_QuizContent_questions => {
  if (!Boolean(currentQuestionId)) {
    return undefined
  }

  const currentQuestionIndex = getCurrentQuestionIndex(state, currentQuestionId)
  if (currentQuestionIndex > 0) {
    return state.questions[currentQuestionIndex - 1]
  }

  return undefined
}

export const hasNextQuestion = (
  state: ReducerState,
  currentQuestionId?: string
): boolean => typeof getNextQuestion(state, currentQuestionId) !== 'undefined'

/**
 * Used to search for the next question.
 * If the current one is undefined than the user is starting the quiz.
 * If the current one is defined
 *  - it should be used to search for the next one
 *  - if there isn't a next question, than we should search for unanswered & required questions (if option is enabled)
 */
export const getNextQuestion = (
  state: ReducerState,
  currentQuestionId?: string
): undefined | AppQuiz_content_QuizContent_questions => {
  if (typeof currentQuestionId === 'undefined') {
    return state.questions[0]
  }

  const currentQuestionIndex = getCurrentQuestionIndex(state, currentQuestionId)
  if (currentQuestionIndex < size(state.questions) - 1) {
    return state.questions[currentQuestionIndex + 1]
  }

  if (currentQuestionIndex === size(state.questions) - 1) {
    const answered = keys(state.answers)
    const firstUnansweredIndex = findIndex(
      state.questions,
      ({ id, canBeSkipped }: any) => !answered.includes(id) && !canBeSkipped
    )

    // the user arrived to the end of questions
    // if the creator enabled appendRequiredQuestions options
    // than we should search from the beginning if there are
    // unanswered questions
    if (
      firstUnansweredIndex > -1 &&
      Boolean(state.content?.appendRequiredQuestions)
    ) {
      return state.questions[firstUnansweredIndex]
    }
  }

  return undefined
}

/**
 * Quiz is completed when there's no more remaining time or user
 * has answered to all the questions.
 */
export const isCompleted = (state: ReducerState) =>
  Boolean(state.completed) ||
  (state.content?.timeLimit > 0 &&
    typeof state.remainingTime !== 'undefined' &&
    state.remainingTime < 1)

/**
 * State initialization must consider options basket and randomization.
 */
const start = (state: ReducerState) => {
  let questions = cloneDeep(state.content.questions)

  questions = questions.map((question) => {
    if (question.__typename === 'QuizContentQuestionMatching') {
      const subquestions = shuffle(map(question.matchPairs, 'subquestion'))
      const subanswers = shuffle(map(question.matchPairs, 'subanswer'))

      return {
        ...question,
        matchPairs: subquestions.map((subquestion, index) => {
          return {
            subanswer: subanswers[index],
            subquestion,
          }
        }),
      }
    }

    return question
  }) as any

  // if questions should be randomized or a basket is wanted
  // than we must randomize the questions order
  if (Boolean(state.content?.randomOrder) || Boolean(state.content?.basket)) {
    questions = shuffle(questions)
  }

  // get a slice of shuffled questions for the basket
  if (state.content?.basket > 0) {
    questions = slice(questions, 0, state.content.basket)
  }

  return {
    ...state,
    answers: {},
    completed: false,
    content: {
      ...state.content,
      session: undefined,
    },
    points: {},
    currentQuestionId: questions.length > 0 ? questions[0].id : null,
    questions,
    remainingTime: state.content?.timeLimit || undefined,
    sessionId: uuidv4(),
    startedAt: new Date(),
  }
}

// validation and points

export const getPointsForEssay = (question: AppQuizUsageQuestionEssay) => {
  return question.missingPoints || 0
}

export const isEssayCompletedWithSuccess = (
  question: AppQuizUsageQuestionEssay,
  content: any
) => {
  const essayPoints = JSON.parse(content?.session?.points)[question.id]
  return essayPoints >= question.successPoints
}

export const isMatchingCompletedWithSuccess = (
  content: any,
  question: AppQuizUsageQuestionMatching,
  answer: any
) => {
  const originalMatchPairs = find(
    content.questions,
    ({ __typename, id }) =>
      __typename === 'QuizContentQuestionMatching' && id === question.id
  ) as any

  const originalMatches = mapValues(
    keyBy(originalMatchPairs?.matchPairs, 'subquestion'),
    'subanswer'
  )

  return isEqual(originalMatches, answer)
}

export const getPointsForMatching = (
  content,
  question: AppQuizUsageQuestionMatching,
  answer: any
) => {
  if (!Boolean(answer)) {
    return question.missingPoints || 0
  }

  return isMatchingCompletedWithSuccess(content, question, answer)
    ? question.successPoints || 1
    : question.failurePoints || 0
}

export const isMissingWordCompletedWithSuccess = (
  question: AppQuizUsageQuestionMissingWord,
  answer: any
) => {
  const questionMatches = question.title
    .match(/(\{=[^\}]+\})/g)
    .map((questionMatch) => {
      const rawMatch = questionMatch.substring(2, questionMatch.length - 1)

      return filter(split(rawMatch, '=').map(trim))
    })

  return (
    size(
      filter(answer || [], (part, index) =>
        questionMatches[index].includes(part)
      )
    ) === size(questionMatches)
  )
}

export const getPointsForMissingWord = (
  question: AppQuizUsageQuestionMissingWord,
  answer: any
) => {
  if (!Boolean(answer)) {
    return question.missingPoints || 0
  }

  return isMissingWordCompletedWithSuccess(question, answer)
    ? question.successPoints || 1
    : question.failurePoints || 0
}

export const isMultipleChoiceCompletedWithSuccess = (
  question: AppQuizUsageQuestionMultipleChoice,
  answer: any
) => {
  if (isArray(answer)) {
    const c = question.choices.map((choice, index) => ({
      id: index,
      text: choice.text,
      isCorrect: choice.isCorrect,
    }))
    const choices = map(
      filter(c, ({ isCorrect }) => isCorrect),
      (choice) => ({ id: choice.id, value: choice.text })
    )
    return (
      choices.length === answer.length &&
      isEqual(
        choices,
        answer.sort((a, b) => a.id - b.id)
      )
    )
  }

  return isEqual(
    answer,
    map(
      filter(question.choices, ({ isCorrect }) => isCorrect),
      'text'
    )
  )
}

export const getPointsForMultipleChoice = (
  question: AppQuizUsageQuestionMultipleChoice,
  answer: any
) => {
  if (!Boolean(answer)) {
    return question.missingPoints || 0
  }

  return isMultipleChoiceCompletedWithSuccess(question, answer)
    ? question.successPoints || 1
    : question.failurePoints || 0
}

export const isNumericalCompletedWithSuccess = (
  question: AppQuizUsageQuestionNumerical,
  answer: any
) => {
  return (
    answer >= question.number - (question.range || 0) &&
    answer <= question.number + (question.range || 0)
  )
}

export const getPointsForNumerical = (
  question: AppQuizUsageQuestionNumerical,
  answer: any
) => {
  if (!Boolean(answer)) {
    return question.missingPoints || 0
  }

  return isNumericalCompletedWithSuccess(question, answer)
    ? question.successPoints || 1
    : question.failurePoints || 0
}

export const isSingleChoiceCompletedWithSuccess = (
  question: AppQuizUsageQuestionSingleChoice,
  answer: any
) => {
  return answer === find(question.choices, ({ isCorrect }) => isCorrect)?.text
}

export const getPointsForSingleChoice = (
  question: AppQuizUsageQuestionSingleChoice,
  answer: any
) => {
  if (!Boolean(answer)) {
    return question.missingPoints || 0
  }

  return isSingleChoiceCompletedWithSuccess(question, answer)
    ? question.successPoints || 1
    : question.failurePoints || 0
}

export const isTrueFalseCompletedWithSuccess = (
  question: AppQuizUsageQuestionTrueFalse,
  answer: any
) => {
  return answer === question.isTrue
}

export const getPointsForTrueFalse = (
  question: AppQuizUsageQuestionTrueFalse,
  answer: any
) => {
  if (typeof answer === 'undefined') {
    return question.missingPoints || 0
  }

  return isTrueFalseCompletedWithSuccess(question, answer)
    ? question.successPoints || 1
    : question.failurePoints || 0
}

export const getTotalPoints = (state: ReducerState) => {
  if (state.content.successPoints > 0) {
    return state.content.successPoints
  }

  return size(state.questions)
}
